import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ImportedProduct, ProductCapture } from "./types";
import type { ExtractedProduct } from "./productImport";

const STORAGE_KEY = "room-canvas:products:v1";
const STORAGE_VERSION = 1;

function uid(prefix = "p"): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function trimName(input: string | undefined, fallback: string): string {
  const t = (input ?? "").trim();
  return t.length > 0 ? t : fallback;
}

/**
 * Where the personal product catalog is currently being persisted.
 * Identical semantics to {@link import("./library").CloudStatus} on the
 * saved-rooms library — we duplicate the type so the two stores can drift
 * independently without coupling.
 */
export type ProductsCloudStatus =
  | "local-only"
  | "syncing"
  | "synced"
  | "error";

interface ProductsState {
  /** All imported products, newest-first by `importedAt`. */
  products: ImportedProduct[];

  /** Current cloud-sync status. Driven by the productsSync orchestrator. */
  cloudStatus: ProductsCloudStatus;

  /** Human-readable error from the most recent failed cloud op, or null.
   *  Set by the sync orchestrator alongside `cloudStatus === 'error'`. */
  cloudError: string | null;

  /**
   * Take an {@link ExtractedProduct} (Cloud Function output) and add it to
   * the catalog. Returns the new `ImportedProduct.id` so the caller can
   * pass it straight to `addFurniture` / `addMount` if they want to place
   * the product on the canvas in the same gesture.
   */
  addExtracted: (extracted: ExtractedProduct) => string;

  /** Replace an existing product wholesale. Bumps `updatedAt`. No-ops if
   *  the id is unknown. */
  updateProduct: (id: string, patch: Partial<ImportedProduct>) => void;

  /** Set or turn a furniture product's 3D model in place (the list order
   *  stays). Bumps `updatedAt`. */
  setProductCapture: (id: string, capture: ProductCapture) => void;

  /** Rename an imported product. */
  renameProduct: (id: string, name: string) => void;

  /** Delete a single imported product. */
  removeProduct: (id: string) => void;

  /** Wipe the entire catalog. */
  clearAll: () => void;

  /**
   * Replace the entire products list. Used by the cloud-sync orchestrator
   * when reconciling a remote snapshot into local state. Kept separate
   * from the mutators above so the orchestrator can suppress the echo
   * write-back that would otherwise create a loop.
   */
  _setProductsFromSync: (products: ImportedProduct[]) => void;

  /** Cloud-sync orchestrator hook — update the surface status. */
  _setCloudStatus: (
    status: ProductsCloudStatus,
    error?: string | null
  ) => void;
}

export const useProducts = create<ProductsState>()(
  persist(
    (set) => ({
      products: [],
      cloudStatus: "local-only",
      cloudError: null,

      addExtracted: (extracted) => {
        const id = uid();
        const now = Date.now();
        const product: ImportedProduct = {
          id,
          kind: extracted.kind,
          name: trimName(extracted.name, "Imported product"),
          width: extracted.width,
          depth: extracted.depth,
          height: extracted.height,
          category: extracted.category,
          color: extracted.color,
          sourceUrl: extracted.sourceUrl,
          sourceDomain: extracted.sourceDomain,
          importedAt: now,
          updatedAt: now,
          needsManualReview: extracted.needsManualReview,
        };
        if (extracted.notes !== undefined) product.notes = extracted.notes;
        if (extracted.metadata !== undefined) {
          product.metadata = structuredClone(extracted.metadata);
        }
        if (extracted.template !== undefined) product.template = extracted.template;
        if (extracted.templateParams !== undefined) {
          product.templateParams = { ...extracted.templateParams };
        }
        if (extracted.material !== undefined) product.material = extracted.material;
        if (extracted.palette !== undefined) product.palette = [...extracted.palette];
        if (extracted.materials !== undefined) product.materials = [...extracted.materials];
        if (extracted.dimensionSource !== undefined) {
          product.dimensionSource = extracted.dimensionSource;
        }
        if (extracted.capture && extracted.kind === "furniture") {
          product.capture = { ...extracted.capture };
        }
        set((s) => ({ products: [product, ...s.products] }));
        return id;
      },

      updateProduct: (id, patch) =>
        set((s) => {
          const idx = s.products.findIndex((p) => p.id === id);
          if (idx === -1) return s;
          const existing = s.products[idx];
          // `id` and `importedAt` are immutable — surface that by ignoring
          // them in the patch rather than throwing.
          const merged: ImportedProduct = {
            ...existing,
            ...patch,
            id: existing.id,
            importedAt: existing.importedAt,
            updatedAt: Date.now(),
          };
          const without = s.products.filter((p) => p.id !== id);
          return { products: [merged, ...without] };
        }),

      setProductCapture: (id, capture) =>
        set((s) => ({
          products: s.products.map((p) =>
            p.id === id && p.kind === "furniture"
              ? { ...p, capture: { ...capture }, updatedAt: Date.now() }
              : p
          ),
        })),

      renameProduct: (id, name) =>
        set((s) => ({
          products: s.products.map((p) =>
            p.id === id
              ? { ...p, name: trimName(name, p.name), updatedAt: Date.now() }
              : p
          ),
        })),

      removeProduct: (id) =>
        set((s) => ({ products: s.products.filter((p) => p.id !== id) })),

      clearAll: () => set({ products: [] }),

      _setProductsFromSync: (products) =>
        set({
          products: products
            .slice()
            .sort((a, b) => b.importedAt - a.importedAt),
        }),

      _setCloudStatus: (status, error = null) =>
        set({ cloudStatus: status, cloudError: error }),
    }),
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      // Persist only `products` to localStorage. Cloud status is recomputed
      // on every page load (it depends on the live auth state) so caching
      // a stale value would just lie to the user.
      partialize: (s) => ({ products: s.products }),
    }
  )
);
